// packages/ui/src/tools/terminal/commandHistory.ts
import { create } from 'zustand';

export interface CommandHistoryEntry {
  command: string;
  count: number;
  /** epoch ms of the most recent run. */
  lastUsed: number;
}

interface HistoryState {
  entries: CommandHistoryEntry[];
  record(command: string): void;
}

/** Persist up to this many distinct commands. */
export const HISTORY_STORE_CAP = 100;
/** Surface at most this many suggestions. */
export const HISTORY_SUGGEST_LIMIT = 20;

const KEY = 'slate:terminalCommandHistory:v1';

function load(): CommandHistoryEntry[] {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { entries?: unknown };
    if (!parsed || !Array.isArray(parsed.entries)) return [];
    return parsed.entries.filter(
      (e): e is CommandHistoryEntry =>
        !!e &&
        typeof (e as CommandHistoryEntry).command === 'string' &&
        typeof (e as CommandHistoryEntry).count === 'number' &&
        typeof (e as CommandHistoryEntry).lastUsed === 'number',
    );
  } catch {
    return [];
  }
}

function persist(entries: CommandHistoryEntry[]): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify({ entries }));
  } catch {
    /* private-mode / quota, best-effort */
  }
}

/** Sort by count desc, then most-recent desc. */
function sortEntries(entries: CommandHistoryEntry[]): CommandHistoryEntry[] {
  return [...entries].sort((a, b) => b.count - a.count || b.lastUsed - a.lastUsed);
}

export const useTerminalCommandHistory = create<HistoryState>((set, get) => ({
  entries: load(),
  record(command) {
    const cmd = command.trim();
    if (cmd.length === 0) return;
    const now = Date.now();
    const existing = get().entries;
    const idx = existing.findIndex((e) => e.command === cmd);
    let next: CommandHistoryEntry[];
    if (idx >= 0) {
      next = existing.slice();
      next[idx] = { ...next[idx], count: next[idx].count + 1, lastUsed: now };
    } else {
      next = [...existing, { command: cmd, count: 1, lastUsed: now }];
    }
    next = sortEntries(next).slice(0, HISTORY_STORE_CAP);
    persist(next);
    set({ entries: next });
  },
}));

/** Imperative helper for non-component callers (the palette command's run). */
export function recordTerminalCommand(command: string): void {
  useTerminalCommandHistory.getState().record(command);
}

/**
 * The suggestions to show for a typed `query`. Empty query returns the overall
 * top commands; otherwise filters to entries containing `query`
 * (case-insensitive). Pure, exported for testing and palette use.
 */
export function topCommands(
  entries: CommandHistoryEntry[],
  query: string,
  limit: number = HISTORY_SUGGEST_LIMIT,
): CommandHistoryEntry[] {
  const sorted = sortEntries(entries);
  const q = query.trim().toLowerCase();
  const filtered = q ? sorted.filter((e) => e.command.toLowerCase().includes(q)) : sorted;
  return filtered.slice(0, limit);
}
